/**
 * Polling with exponential backoff + a "last published" freshness
 * indicator (requirement 2). This is the *only* form of "liveness" the
 * page has (§A.1 rule 3: "live-ness comes from the browser polling the
 * manifest, never from an endpoint") — it just re-fetches `latest.json`
 * on an interval and reports whether the version changed.
 */

export type PollStatus = "ok" | "unchanged" | "error" | "not-published-yet";

export interface PollSnapshot<T> {
  readonly status: PollStatus;
  readonly value: T | null;
  readonly lastSuccessAt: number | null;
  readonly lastError: Error | null;
  readonly consecutiveErrors: number;
}

export type PollListener<T> = (snapshot: PollSnapshot<T>) => void;

export interface PollerOptions {
  readonly intervalMs: number;
  readonly backoffMaxMs: number;
}

/** Polls `fetchOne` on an interval, backing off geometrically (capped at
 * `backoffMaxMs`) after consecutive failures, and notifying `listener` with
 * every attempt's outcome (never throws out of the poll loop itself — a
 * failure is reported to the listener as a `PollSnapshot`, not an
 * unhandled rejection, since a static host returning a transient 5xx must
 * not take down the page). */
export class Poller<T> {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private consecutiveErrors = 0;
  private lastSuccessAt: number | null = null;
  private lastValue: T | null = null;
  private lastKey: string | null = null;

  constructor(
    private readonly fetchOne: () => Promise<{ key: string; value: T }>,
    private readonly listener: PollListener<T>,
    private readonly options: PollerOptions,
    private readonly isNotPublishedYet: (error: unknown) => boolean,
  ) {}

  start(): void {
    this.stopped = false;
    void this.tick();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) clearTimeout(this.timer);
  }

  private async tick(): Promise<void> {
    if (this.stopped) return;
    try {
      const { key, value } = await this.fetchOne();
      this.consecutiveErrors = 0;
      this.lastSuccessAt = Date.now();
      const status: PollStatus = key === this.lastKey ? "unchanged" : "ok";
      this.lastKey = key;
      this.lastValue = value;
      this.listener({
        status,
        value,
        lastSuccessAt: this.lastSuccessAt,
        lastError: null,
        consecutiveErrors: 0,
      });
    } catch (error) {
      if (this.isNotPublishedYet(error)) {
        this.listener({
          status: "not-published-yet",
          value: this.lastValue,
          lastSuccessAt: this.lastSuccessAt,
          lastError: null,
          consecutiveErrors: 0,
        });
        this.consecutiveErrors = 0;
      } else {
        this.consecutiveErrors += 1;
        this.listener({
          status: "error",
          value: this.lastValue,
          lastSuccessAt: this.lastSuccessAt,
          lastError: error instanceof Error ? error : new Error(String(error)),
          consecutiveErrors: this.consecutiveErrors,
        });
      }
    }
    if (this.stopped) return;
    const delay = this.nextDelayMs();
    this.timer = setTimeout(() => void this.tick(), delay);
  }

  private nextDelayMs(): number {
    if (this.consecutiveErrors === 0) return this.options.intervalMs;
    const backoff = this.options.intervalMs * 2 ** this.consecutiveErrors;
    return Math.min(backoff, this.options.backoffMaxMs);
  }
}

/** Human-readable "last published Ns ago" for the freshness indicator. */
export function freshnessLabel(lastSuccessAt: number | null, now: number = Date.now()): string {
  if (lastSuccessAt === null) return "never published";
  const seconds = Math.max(0, Math.round((now - lastSuccessAt) / 1000));
  if (seconds < 2) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return `${hours}h ago`;
}

/** Owner requirement 5 ("realtime feel"): a "live · N s ago" freshness
 * badge — wired into `LogPanel`'s footer next to the follow-live toggle.
 * At least one successful poll ever having landed is what "live" means
 * here (a static host has no server-pushed liveness — see the polling.ts
 * module doc comment); a genuine fetch error doesn't blank the badge, it
 * keeps showing staleness against the last real success so the user can
 * tell the feed is stuck rather than just quiet. */
export function freshnessBadgeLabel(
  status: PollStatus,
  lastSuccessAt: number | null,
  now: number = Date.now(),
): string {
  if (lastSuccessAt === null) {
    return status === "not-published-yet" ? "not published yet" : "no data yet";
  }
  return `live · ${freshnessLabel(lastSuccessAt, now)}`;
}
